import React, { useState, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const TOUR_KEY = (userId) => `qg_sales_tutorial_done_${userId}`;

const SERVICE_CATEGORIES = [
  { key: 'web_design',   label: 'Web Design' },
  { key: 'seo',          label: 'SEO' },
  { key: 'social_media', label: 'Social Media' },
  { key: 'branding',     label: 'Branding' },
  { key: 'mobile_app',   label: 'Mobile App' },
  { key: 'ecommerce',    label: 'E-Commerce' },
  { key: 'ppc',          label: 'PPC / Paid Ads' },
  { key: 'ai_chatbot',   label: 'AI Chatbot' },
  { key: 'hosting',      label: 'Hosting' },
  { key: 'content',      label: 'Content' },
  { key: 'other',        label: 'Other' },
];

function Profile() {
  const { user, profile, setProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    company_name: profile?.company_name || '',
  });

  const [specialties, setSpecialties] = useState(() => {
    const s = profile?.specialties;
    if (Array.isArray(s)) return s;
    try { return JSON.parse(s || '[]'); } catch { return []; }
  });
  const [savingSpecialties, setSavingSpecialties] = useState(false);
  const [specialtiesSuccess, setSpecialtiesSuccess] = useState('');
  const [tourRestarted, setTourRestarted] = useState(false);

  const isSalesRep = profile?.role === 'sales_rep';

  const handleRestartTour = () => {
    if (user?.id) {
      localStorage.removeItem(TOUR_KEY(user.id));
      setTourRestarted(true);
      setTimeout(() => setTourRestarted(false), 4000);
    }
  };

  const isContractor = profile?.role === 'contractor' || profile?.role === 'contractor_admin';

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: form.full_name,
          phone: form.phone,
          company_name: form.company_name,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setSuccess('Profile updated successfully!');
      setEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialty = (key) => {
    setSpecialties(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const saveSpecialties = useCallback(async () => {
    setSavingSpecialties(true);
    try {
      const { error: err } = await supabase
        .from('profiles')
        .update({ specialties, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (err) throw err;
      setSpecialtiesSuccess('Specialties saved!');
      setTimeout(() => setSpecialtiesSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save specialties:', err);
    } finally {
      setSavingSpecialties(false);
    }
  }, [user.id, specialties]);

  const roleLabel = {
    client: 'Client',
    sales_rep: 'Sales Representative',
    admin: 'Administrator',
    contractor: 'Contractor',
    contractor_admin: 'Contractors Admin',
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-primary">My Profile</h2>
        <p className="text-secondary mt-1">View and manage your account information</p>
      </div>

      {success && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg text-sm">
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="bg-input border border-theme rounded-xl p-6">
        <div className="flex items-center space-x-4 mb-6 pb-6 border-b border-theme">
          <div className="w-16 h-16 rounded-full bg-yellow-500/10 border-2 border-yellow-500/20 flex items-center justify-center">
            <span className="text-yellow-500 text-2xl font-bold">
              {profile?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-primary">{profile?.full_name || 'User'}</h3>
            <p className="text-yellow-500 text-sm font-medium">{roleLabel[profile?.role] || profile?.role}</p>
            <p className="text-muted text-sm">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full bg-card border border-theme-hover text-muted rounded-lg px-4 py-3 cursor-not-allowed"
            />
            <p className="text-xs text-muted mt-1">Email cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Role</label>
            <input
              type="text"
              value={roleLabel[profile?.role] || profile?.role || ''}
              disabled
              className="w-full bg-card border border-theme-hover text-muted rounded-lg px-4 py-3 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Full Name</label>
            <input
              type="text"
              name="full_name"
              value={form.full_name}
              onChange={handleChange}
              disabled={!editing}
              className={`w-full bg-card border border-theme-hover rounded-lg px-4 py-3 focus:outline-none transition-colors ${
                editing ? 'text-primary focus:border-yellow-500' : 'text-secondary cursor-not-allowed'
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Phone</label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              disabled={!editing}
              placeholder="Enter your phone number"
              className={`w-full bg-card border border-theme-hover rounded-lg px-4 py-3 focus:outline-none transition-colors placeholder-muted ${
                editing ? 'text-primary focus:border-yellow-500' : 'text-secondary cursor-not-allowed'
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Company Name</label>
            <input
              type="text"
              name="company_name"
              value={form.company_name}
              onChange={handleChange}
              disabled={!editing}
              placeholder="Enter your company name"
              className={`w-full bg-card border border-theme-hover rounded-lg px-4 py-3 focus:outline-none transition-colors placeholder-muted ${
                editing ? 'text-primary focus:border-yellow-500' : 'text-secondary cursor-not-allowed'
              }`}
            />
          </div>

          {profile?.referral_code && (
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">Referral Code</label>
              <input
                type="text"
                value={profile.referral_code}
                disabled
                className="w-full bg-card border border-theme-hover text-yellow-500 rounded-lg px-4 py-3 cursor-not-allowed font-mono"
              />
            </div>
          )}

          <div className="flex items-center space-x-3 pt-2">
            {editing ? (
              <>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setForm({
                      full_name: profile?.full_name || '',
                      phone: profile?.phone || '',
                      company_name: profile?.company_name || '',
                    });
                    setError('');
                  }}
                  className="px-6 py-3 border border-theme-hover text-secondary rounded-lg hover:text-primary hover:border-theme-hover transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="px-6 py-3 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors"
              >
                Edit Profile
              </button>
            )}
          </div>
        </form>
      </div>

      {/* My Specialties — contractor only */}
      {isContractor && (
        <div className="bg-input border border-theme rounded-xl p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-semibold text-primary">My Specialties</h3>
            {specialtiesSuccess && (
              <span className="text-xs text-green-400 font-medium">{specialtiesSuccess}</span>
            )}
          </div>
          <p className="text-xs text-muted mb-4">
            Select the service categories you specialise in. You'll only receive job notifications for matching categories.
            Leave all unchecked to receive notifications for every job.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {SERVICE_CATEGORIES.map(cat => {
              const active = specialties.includes(cat.key);
              return (
                <button
                  key={cat.key}
                  onClick={() => toggleSpecialty(cat.key)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all text-left ${
                    active
                      ? 'bg-yellow-500/15 border-yellow-500/40 text-yellow-400'
                      : 'bg-white/3 border-white/10 text-secondary hover:border-white/20 hover:text-primary'
                  }`}
                >
                  <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                    active ? 'bg-yellow-500 border-yellow-500' : 'border-white/30'
                  }`}>
                    {active && (
                      <svg className="w-2.5 h-2.5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {cat.label}
                </button>
              );
            })}
          </div>
          {specialties.length === 0 && (
            <p className="text-xs text-yellow-500/70 mb-3">All unchecked — you'll see notifications for all job types.</p>
          )}
          <button
            onClick={saveSpecialties}
            disabled={savingSpecialties}
            className="px-5 py-2 bg-yellow-500 text-black text-sm font-semibold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50"
          >
            {savingSpecialties ? 'Saving…' : 'Save Specialties'}
          </button>
        </div>
      )}

      {isSalesRep && (
        <div className="bg-input border border-theme rounded-xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-1">Dashboard Tour</h3>
              <p className="text-sm text-secondary">
                Restart the onboarding walkthrough to revisit how the platform works — leads, pipeline, commissions, and payouts.
              </p>
              {tourRestarted && (
                <p className="mt-2 text-xs text-green-400 font-medium">
                  Tour reset. It will start automatically next time you visit your dashboard.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleRestartTour}
              disabled={tourRestarted}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-yellow-500/40 text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {tourRestarted ? 'Tour Reset!' : 'Restart Tour'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-input border border-theme rounded-xl p-6">
        <h3 className="text-lg font-semibold text-primary mb-2">Account Information</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-secondary">Member Since</span>
            <span className="text-primary">
              {profile?.created_at
                ? new Date(profile.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '-'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-secondary">Account ID</span>
            <span className="text-muted font-mono text-xs">{user?.id?.slice(0, 8) || '-'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
